<script setup>
import { computed } from 'vue';
import CFIcon from '@/ui/Icon/CFIcon.vue';
import CFInlineSpinner from '@/ui/Spinner/CFInlineSpinner.vue';
import theme, { buttonColors, buttonSizes, buttonVariants } from './index.js';

const props = defineProps({
    /**
     * @defaultValue 'primary'
     */
    color: {
        type: String,
        default: 'primary',
        validator: (v) => buttonColors.includes(v),
    },
    /**
     * @defaultValue 'md'
     */
    size: {
        type: String,
        default: 'md',
        validator: (v) => buttonSizes.includes(v),
    },
    /**
     * @defaultValue 'solid'
     */
    variant: {
        type: String,
        default: 'solid',
        validator: (v) => buttonVariants.includes(v),
    },
    /**
     * Display an icon based on the 'leading' and 'trailing' props.
     */
    icon: {
        /** @type import('vue').PropType<IconName> */
        type: String,
        default: null,
    },
    type: {
        type: String,
        default: 'button',
        validator: (v) => ['button', 'submit', 'reset'].includes(v),
    },
    /**
     * When true, the loading icon will be displayed.
     * @defaultValue false
     */
    loading: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    /**
     * If true, icon will be placed at the start of the button
     * @defaultValue true
     */
    leading: {
        type: Boolean,
        default: true,
    },
    /**
     * If true, icon will be placed at the end of the button
     * @defaultValue false
     */
    trailing: {
        type: Boolean,
        default: false,
    },
    /**
     * Render the button full width
     * @defaultValue false
     */
    block: {
        type: Boolean,
        default: false,
    },
    /**
     * Used to override the slots styles
     */
    ui: {
        type: Object,
        default: () => ({}),
    },
});

const ui = computed(() =>
    theme({
        color: props.color,
        variant: props.variant,
        size: props.size,
        disabled: props.disabled,
        loading: props.loading,
        block: props.block,
        leading: props.leading,
        trailing: props.trailing,
        icon: !!props.icon,
    })
);
</script>

<template>
    <button
        :type="type"
        :class="ui.base({ class: [props.ui?.base, props.class] })"
        :disabled="disabled || loading"
    >
        <span
            v-if="loading"
            :class="ui.loadingIndicator({ class: props.ui?.loadingIndicator })"
            role="status"
            data-loading-indicator
        >
            <CFInlineSpinner :class="ui.loadingIcon({ class: props.ui?.loadingIcon })" />
            <span class="sr-only">{{ $t('general.loading') }}...</span>
        </span>

        <slot name="leading">
            <CFIcon
                v-if="icon && !trailing"
                :name="icon"
                :class="ui.leadingIcon({ class: props.ui?.leadingIcon })"
            />
            <span
                v-else-if="size === 'xl' || block"
                aria-hidden="true"
                :class="ui.leadingIcon({ class: props.ui?.iconPlaceholder })"
            ></span>
        </slot>

        <span :class="ui.label({ class: props.ui?.label })">
            <slot />
        </span>

        <slot name="trailing">
            <CFIcon
                v-if="icon && trailing"
                :name="icon"
                :class="ui.trailingIcon({ class: props.ui?.trailingIcon })"
            />
            <span
                v-else-if="size === 'xl' || block"
                aria-hidden="true"
                :class="ui.trailingIcon({ class: props.ui?.iconPlaceholder })"
            ></span>
        </slot>
    </button>
</template>
